/**
 * Página pública /agencia-actualizar/:token
 *
 * Sin login. La agencia abre el link que el admin le envió por WhatsApp/correo,
 * ve sus datos precargados, edita los campos suyos (no comisiones ni crédito),
 * envía → los cambios entran como "pendiente" para revisión del admin.
 *
 * Mobile-first.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, Stack, IconButton, Alert, Chip,
  CircularProgress, Switch, FormControlLabel, MenuItem, Divider,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

export default function AgenciaActualizar() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datos, setDatos] = useState(null);
  const [form, setForm] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [exito, setExito] = useState(false);
  const [expiraAt, setExpiraAt] = useState(null);
  const [esPrimeraVez, setEsPrimeraVez] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE}/turismo/agencia-actualizar/${token}`)
      .then(async r => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || 'No pudimos cargar este enlace.');
        }
        return r.json();
      })
      .then(d => {
        setDatos(d.agencia);
        setForm({
          nombre: d.agencia.nombre || '',
          tipo: d.agencia.tipo || 'juridica',
          nit_cedula: d.agencia.nit_cedula || '',
          direccion: d.agencia.direccion || '',
          pais: d.agencia.pais || '',
          ciudad: d.agencia.ciudad || '',
          telefono_general: d.agencia.telefono_general || '',
          correo: d.agencia.correo || '',
          contactos: (d.agencia.contactos || []).map(c => ({ ...c })),
        });
        setExpiraAt(d.expira_at);
        setEsPrimeraVez(!!d.es_primera_vez);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleContactoChange = (idx, field, value) => {
    setForm(f => ({
      ...f,
      contactos: f.contactos.map((c, i) => i === idx ? { ...c, [field]: value } : c),
    }));
  };

  const handleAgregarContacto = () => {
    setForm(f => ({
      ...f,
      contactos: [...f.contactos, {
        nombre: '', numero_whatsapp: '', correo: '', cargo: '',
        identificacion: '', es_principal: false,
      }],
    }));
  };

  const handleQuitarContacto = (idx) => {
    setForm(f => ({
      ...f,
      contactos: f.contactos.filter((_, i) => i !== idx),
    }));
  };

  const handleMarcarPrincipal = (idx) => {
    setForm(f => ({
      ...f,
      contactos: f.contactos.map((c, i) => ({ ...c, es_principal: i === idx })),
    }));
  };

  const enviar = async () => {
    if (!form.nombre.trim()) { alert('El nombre es obligatorio.'); return; }
    if (form.contactos.length === 0) {
      if (!window.confirm('No tienes contactos registrados. ¿Continuar de todas formas?')) return;
    }
    // Validar al menos un principal si hay contactos
    if (form.contactos.length > 0 && !form.contactos.some(c => c.es_principal)) {
      if (!window.confirm('Ningún contacto está marcado como principal. ¿Continuar?')) return;
    }
    setEnviando(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/agencia-actualizar/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || 'Error al enviar la solicitud.');
      }
      setExito(true);
    } catch (e) {
      alert(e.message);
    } finally {
      setEnviando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
        <CircularProgress sx={{ color: '#0d9488' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 4 }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: '#dc2626', mb: 2 }} />
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a', mb: 1 }}>
            Enlace no disponible
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#64748b' }}>{error}</Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 2 }}>
            Si crees que es un error, contacta al equipo de Entretur para que te genere uno nuevo.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (exito) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 480, textAlign: 'center', borderRadius: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 72, color: '#16a34a', mb: 2 }} />
          <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', mb: 1 }}>
            ¡Solicitud enviada!
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#475569', mb: 2 }}>
            Tu información fue recibida correctamente. El equipo de Entretur revisará los cambios
            y los aplicará pronto.
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
            Puedes cerrar esta página.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: { xs: 1, md: 3 } }}>
      <Box sx={{ maxWidth: 700, mx: 'auto', px: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Paper sx={{
          p: 2.5, mb: 2, borderRadius: 3,
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
          color: '#fff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <BusinessIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 11, opacity: 0.9, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5 }}>
                {esPrimeraVez ? '¡Te damos la bienvenida!' : 'Actualización de datos'}
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
                {datos.nombre}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
          {esPrimeraVez ? (
            <>
              <b>Bienvenido a Entretur.</b> Para activar tu agencia como vendedora,
              completa los datos del formulario y agrega al menos un vendedor con su WhatsApp.
              El equipo de Entretur validará la información y activará tu cuenta.
            </>
          ) : (
            <>Revisa y actualiza tus datos. Cuando envíes, el equipo de Entretur recibirá la solicitud
              y aplicará los cambios después de validarla.</>
          )}
          {expiraAt && (
            <Typography sx={{ fontSize: 11, mt: 0.5, opacity: 0.85 }}>
              Este enlace vence el {new Date(expiraAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })}.
            </Typography>
          )}
        </Alert>

        {/* Datos de la agencia */}
        <Paper sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
          <Typography sx={{ fontSize: 11, color: '#0d9488', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
            Datos de tu agencia
          </Typography>
          <Stack spacing={1.5}>
            <TextField size="small" label="Razón social / Nombre *" value={form.nombre}
              onChange={e => setForm({ ...form, nombre: e.target.value })} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 1.5 }}>
              <TextField size="small" select label="Tipo" value={form.tipo}
                onChange={e => setForm({ ...form, tipo: e.target.value })} fullWidth>
                <MenuItem value="juridica">Persona jurídica</MenuItem>
                <MenuItem value="natural">Persona natural</MenuItem>
              </TextField>
              <TextField size="small" label={form.tipo === 'natural' ? 'Cédula' : 'NIT'}
                value={form.nit_cedula}
                onChange={e => setForm({ ...form, nit_cedula: e.target.value })} fullWidth />
            </Box>
            <TextField size="small" label="Dirección" value={form.direccion}
              onChange={e => setForm({ ...form, direccion: e.target.value })} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField size="small" label="País" value={form.pais}
                onChange={e => setForm({ ...form, pais: e.target.value })} fullWidth />
              <TextField size="small" label="Ciudad" value={form.ciudad}
                onChange={e => setForm({ ...form, ciudad: e.target.value })} fullWidth />
            </Box>
            <TextField size="small" label="Teléfono general" value={form.telefono_general}
              onChange={e => setForm({ ...form, telefono_general: e.target.value })} fullWidth
              placeholder="+57 ..." />
            <TextField size="small" label="Correo corporativo" value={form.correo}
              onChange={e => setForm({ ...form, correo: e.target.value })} fullWidth
              type="email" />
          </Stack>
        </Paper>

        {/* Contactos */}
        <Paper sx={{ p: 2.5, mb: 2, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography sx={{ fontSize: 11, color: '#0d9488', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Vendedores / Contactos ({form.contactos.length})
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={handleAgregarContacto}
              sx={{ textTransform: 'none', color: '#0d9488', fontWeight: 700 }}>
              Agregar
            </Button>
          </Box>

          {form.contactos.length === 0 && (
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              No tienes vendedores registrados. Agrega al menos uno con su WhatsApp para que podamos contactarlo.
            </Alert>
          )}

          <Stack spacing={2}>
            {form.contactos.map((c, idx) => (
              <Paper key={idx} variant="outlined" sx={{ p: 1.5, borderRadius: 2,
                borderLeft: c.es_principal ? '4px solid #f59e0b' : '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PersonIcon sx={{ color: c.es_principal ? '#f59e0b' : '#64748b', fontSize: 20 }} />
                  <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                    {c.nombre || `Contacto ${idx + 1}`}
                  </Typography>
                  {c.es_principal && (
                    <Chip size="small" label="Principal"
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 10 }} />
                  )}
                  <IconButton size="small" onClick={() => handleQuitarContacto(idx)} sx={{ color: '#ef4444' }}>
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
                <Stack spacing={1}>
                  <TextField size="small" label="Nombre completo *" value={c.nombre}
                    onChange={e => handleContactoChange(idx, 'nombre', e.target.value)} fullWidth />
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    <TextField size="small" label="WhatsApp *" value={c.numero_whatsapp}
                      onChange={e => handleContactoChange(idx, 'numero_whatsapp', e.target.value)}
                      placeholder="+57 ..."
                      InputProps={{ startAdornment: <WhatsAppIcon sx={{ fontSize: 18, color: '#10b981', mr: 0.5 }} /> }} />
                    <TextField size="small" label="Correo" value={c.correo || ''}
                      onChange={e => handleContactoChange(idx, 'correo', e.target.value)}
                      type="email"
                      InputProps={{ startAdornment: <EmailIcon sx={{ fontSize: 18, color: '#64748b', mr: 0.5 }} /> }} />
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                    <TextField size="small" label="Cargo" value={c.cargo || ''}
                      onChange={e => handleContactoChange(idx, 'cargo', e.target.value)}
                      placeholder="Vendedor, Gerente..." />
                    <TextField size="small" label="Identificación (opcional)" value={c.identificacion || ''}
                      onChange={e => handleContactoChange(idx, 'identificacion', e.target.value)} />
                  </Box>
                  <FormControlLabel control={
                    <Switch checked={!!c.es_principal} onChange={() => handleMarcarPrincipal(idx)} size="small" />
                  } label={<Typography sx={{ fontSize: 12 }}>Marcar como contacto principal</Typography>} />
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>

        <Divider sx={{ my: 2 }} />

        {/* Botón enviar */}
        <Box sx={{ position: 'sticky', bottom: 16, zIndex: 5 }}>
          <Button fullWidth variant="contained" size="large" disabled={enviando}
            onClick={enviar} disableElevation
            sx={{
              py: 1.5, fontSize: 15, fontWeight: 700, textTransform: 'none',
              background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
              boxShadow: '0 8px 24px rgba(13, 148, 136, 0.4)',
              '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' },
              '&:disabled': { background: '#cbd5e1', color: '#fff' },
            }}>
            {enviando ? 'Enviando…' : 'Enviar cambios al equipo de Entretur'}
          </Button>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', mt: 1 }}>
            Los cambios se aplicarán después de revisión.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
